/**
 * Самоличността (роли и права) около локалния режим — двата случая, в които
 * `GET /user/info` трябва да се поиска НАНОВО, без човекът да прави нищо:
 *
 *  • при всяка смяна на режима (§13.1 на договора) — източникът е друг:
 *    в `local` касата сервира отговора от snapshot-а, в `cloud` — базата;
 *  • след вход СРЕЩУ КАСАТА (§16.4 №5) — там първият опит може да се размине
 *    с касата и провалът е тих: `authSlice` остава `failed` с празни роли.
 *
 * Отделено от `edgeMode.ts` (оркестраторът на режима), за да не расте той:
 * тук няма решения за режима, само зареждане на самоличността.
 */
import { fetchUserInfo } from "../../store/authSlice";
import { getAccountIsPersonal } from "../auth";
import { currentToken, devLog, getEdgeRuntime } from "./edgeRuntime";
import { isEdgeLocalMode } from "./edgeState";

// След колко се прави всеки следващ опит за самоличността след локален вход.
// Първият опит е на самия екран за вход (`pages/Login.tsx`), затова тук се
// започва с изчакване; броят на опитите е дължината на списъка.
const LOCAL_IDENTITY_RETRY_DELAYS_MS = [1_000, 3_000, 6_000];

/**
 * §13.1: при всяка смяна на режима самоличността се презарежда от новия
 * източник (`GET /user/info` — касата го сервира от snapshot-а, облакът от
 * базата). Само за влязъл ЧОВЕК: личен акаунт с токен или служител на общ
 * терминал. На ПИН екрана няма кого да опресним — ролите на X никога не
 * влизат в Redux, а провалено възстановяване на служителя гейтът повтаря
 * сам при смяната на режима (`routes/useEmployeeSessionRestore.ts`).
 */
export function refreshIdentityAfterModeSwitch() {
  const runtime = getEdgeRuntime();
  if (!runtime) return;
  const hasPerson =
    getAccountIsPersonal() === false
      ? runtime.store.getState().auth.personal === true
      : currentToken() !== null;
  if (!hasPerson) return;
  void runtime.store.dispatch(fetchUserInfo());
}

/**
 * §16.4 №5: ролите след вход СРЕЩУ КАСАТА. Екранът за вход дърпа
 * `GET /user/info` веднага след `POST {edge}/user/login`, но точно тогава
 * касата може да е заета със замразяването си (`POST {edge}/mode/enter` тече
 * в същия момент) или заявката да се размине с режима и да отиде към мъртвия
 * облак. Провалът е ТИХ: `authSlice` няма кеширана самоличност за този нов
 * токен, значи остава `failed` с празни `roles`/`permissions` — началният
 * екран е без плочки, а `AuthorizedRoute` връща терминала на „Модули" при
 * всеки опит да се отвори „Маркиране". Човекът вижда работещ терминал без
 * достъп до нищо и единственият изход е нов вход.
 *
 * Затова опитът се повтаря, докато самоличността се получи. Спира се при
 * успех, при излизане от локален режим и при смяна на токена (влязъл е друг —
 * този опит вече не е за него).
 */
export async function ensureIdentityAfterLocalLogin(token: string) {
  const runtime = getEdgeRuntime();
  if (!runtime) return;
  for (const delayMs of LOCAL_IDENTITY_RETRY_DELAYS_MS) {
    await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    if (currentToken() !== token || !isEdgeLocalMode()) return;
    const auth = runtime.store.getState().auth;
    // Самоличността е тук (от екрана за вход или от предишен опит).
    if (auth.user) return;
    // Опитът на екрана за вход още тече — изчакваме го, вместо да питаме пак.
    if (auth.status === "loading") continue;
    devLog("identity after local login is missing — asking the till again");
    try {
      await runtime.store.dispatch(fetchUserInfo()).unwrap();
    } catch (error) {
      devLog("identity retry failed", error);
    }
  }
}
